/**
 * `/r/CODE`: kicked notice → join form (no seat yet) → waiting room → game.
 * With a stored seat token the seat is reclaimed silently (a spinner shows
 * while the first view arrives); a room view already on screen stays up
 * through reconnects so the board never flickers away.
 *
 * Every screen change starts at the top of the page, and focus that was lost
 * with the old screen (the Create room / Copy link button that unmounted)
 * lands on the new screen's main content instead of <body>.
 */
import { useEffect, useLayoutEffect } from 'react';
import { focusIsLost, focusMain } from '../lib/focus';
import { getSeatToken } from '../lib/storage';
import { enterRoom } from '../net/session';
import { useRoomStore } from '../state/roomStore';
import { GameScreen } from './GameScreen';
import { JoinForm } from './JoinForm';
import { KickedScreen } from './KickedScreen';
import { WaitingRoom } from './WaitingRoom';

export function RoomScreen({ code }: { code: string }) {
  const view = useRoomStore((s) => s.view);
  const kicked = useRoomStore((s) => s.kicked);
  const joinError = useRoomStore((s) => s.joinError);

  useEffect(() => {
    enterRoom(code);
  }, [code]);

  const roomView = view?.code === code ? view : null;
  // A rematch is a new screen too (`game:2`): the game-over bar's focused
  // Rematch button unmounts when the next game starts.
  const screen = kicked
    ? 'kicked'
    : roomView
      ? roomView.status === 'waiting' || !roomView.game
        ? 'waiting'
        : `game:${roomView.gameNumber}`
      : getSeatToken(code) && !joinError
        ? 'loading'
        : 'join';

  // Layout effect: runs after the new screen's DOM (and its autoFocus) is in
  // place but before paint, so the old scroll offset never flashes.
  useLayoutEffect(() => {
    window.scrollTo(0, 0);
    if (focusIsLost()) focusMain();
  }, [screen]);

  if (kicked) return <KickedScreen kicked={kicked} />;

  if (roomView) {
    if (!roomView.game || screen === 'waiting') return <WaitingRoom view={roomView} />;
    return <GameScreen view={roomView} game={roomView.game} />;
  }

  if (screen === 'loading') {
    return (
      <main className="screen-center" id="main">
        <p className="room-loading" role="status">
          <span className="spinner" aria-hidden="true" />
          Joining room {code}…
        </p>
      </main>
    );
  }

  return <JoinForm code={code} />;
}
